import { asesoriasService } from '@/services/asesorias.service';
import { audiosService } from '@/services/audios.service';
import { dashboardService } from '@/services/dashboard.service';
import { evidenciasService } from '@/services/evidencias.service';
import { requisitosService } from '@/services/requisitos.service';
import { useOptionalAuth } from '@/auth/useAuth';
import { useEvaluacionStore } from '@/stores/useEvaluacionStore';
import { useEvaluacionTernaStore } from '@/stores/useEvaluacionTernaStore';
import { agruparCadenaArchivos } from '@/utils/fileChains';
import { formatDuracionCorta } from '@/utils/formatDuration';
import {
  comentariosRestantesRonda,
  estaAprobado,
  estaResuelto,
  estadoConversacion,
  historialKey,
  type EstadoConversacion,
  type RevisionEntry,
} from '@/utils/revisionHistorial';

/** El asesor puede comentar mientras el elemento siga abierto (rechazado y el admin no aprobó) y le
 * queden comentarios en la ronda. Las subidas de archivo no consumen cupo (son ilimitadas). */
function puedeComentarAsesor(entries: RevisionEntry[]): boolean {
  const est = estadoConversacion(entries);
  if (est === 'APROBADO' || est === 'SIN_ACTIVIDAD') return false;
  return comentariosRestantesRonda(entries) > 0;
}
import {
  AlertCircle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  History,
  Key,
  Link2,
  MessageCircle,
  Save,
  Trash2,
  Upload,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAdminSecretPanel } from '@/hooks/useAdminSecretPanel';
import { useRequestAudioAccess, useRequestFormAccess } from '@/hooks/useAudios';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { CorreccionesChat } from './CorreccionesChat';
import { CorreccionesSplitPanel } from './CorreccionesSplitPanel';
import { NuevaEvidenciaModal } from './NuevaEvidenciaModal';

interface ElementoRechazado {
  id: number;
  elementoTipo: string;
  elementoId: string;
  requisitoId?: string;
  elementoNombre?: string;
  observacion?: string;
}

interface Props {
  elementosRechazados: ElementoRechazado[];
  nit: string;
  asesoriaId: number;
  historialPorElemento: Map<string, RevisionEntry[]>;
  evidencias: any[];
  audios: any[];
  requisitos: any[];
  ternas: any[];
  evaluacionesTernas: any[];
  cambioFieldLogs?: import('@/types/evaluacionAdmin').CambioFieldLog[];
  ntcs: any[];
  refetch: () => void | Promise<void>;
  clavesResueltas?: Set<string>;
  onResponded?: () => void;
  permiteSubirEvidenciasNuevas?: boolean | null;
  correccionesCtx?: any;
  onEliminarEvidencia?: (evidenciaId: number) => Promise<void>;
  onEliminarAudio?: (audioId: number) => Promise<void>;
}

type SubTab = 'matriz' | 'evidencias' | 'audios';

interface MatrizStep {
  key: string;
  ternaDef: any;
  evalTerna?: any;
  rejectedElements: ElementoRechazado[];
}

const LABEL_TIPO: Record<string, string> = {
  REQUISITO: 'Requisito',
  OBSERVACIONES: 'Observaciones de la matriz',
  PLANMEJORA: 'Plan de mejora',
  FECHAIMPLEMENTACION: 'Fecha de implementación',
  EVIDENCIA: 'Evidencia',
  AUDIO: 'Audio',
};

const CAL_STYLES: Record<string, string> = {
  C: 'bg-[#eaf5ee] text-[#2d7a4f] border-[#b7dfc7]',
  NC: 'bg-[#fdf0ee] text-[#c0392b] border-[#f5c6c0]',
  CP: 'bg-[#fffbeb] text-[#92400e] border-[#fde68a]',
  NA: 'bg-gray-100 text-gray-600 border-gray-300',
};

// ── SHARED SUB-COMPONENTS: INDICADORES DE ESTADO DE CONVERSACIÓN ──

const DOT_ESTADO_CONVERSACION: Record<EstadoConversacion, string> = {
  APROBADO: 'bg-[#2d7a4f]',
  RECHAZADO: 'bg-red-500',
  ESPERANDO_ADMIN: 'bg-amber-500',
  ESPERANDO_ASESOR: 'bg-red-500',
  CORREGIDO: 'bg-blue-500',
  SIN_ACTIVIDAD: 'bg-gray-300',
};

function EstadoDot({ estado }: { estado: EstadoConversacion }) {
  return (
    <span data-testid="cc-estado-dot" data-estado={estado} className={`w-2 h-2 rounded-full shrink-0 ${DOT_ESTADO_CONVERSACION[estado]}`} />
  );
}

function PendingBadge({ count }: { count: number }) {
  // No mostramos el número de entradas (era ruido: contaba todo el historial). Basta con
  // señalar que hubo actividad/cambios en la conversación con un punto.
  if (count === 0) return null;
  return <span data-testid="cc-pending-badge" className="w-1.5 h-1.5 rounded-full bg-gray-400 shrink-0" aria-label="Con cambios" />;
}

/**
 * "Rechazado" solo mientras el asesor todavía no respondió — una vez que apela, el elemento
 * sigue visible (no desaparece) pero pasa a "Respuesta enviada" hasta que el admin lo cierre.
 * Sin esto, el badge quedaría diciendo "Rechazado" para siempre aunque ya se haya respondido.
 *
 * Importante: responder a UN elemento (RESPUESTA_ASESOR) no implica que la asesoría completa ya
 * se haya reenviado a revisión — son acciones independientes. Por eso el texto no dice "esperando
 * revisión del administrador": eso sería falso mientras la asesoría siga sin reenviarse.
 */
function EstadoRechazoBadge({ entries }: { entries: RevisionEntry[] }) {
  const necesitaAccion = estadoConversacion(entries) === 'ESPERANDO_ASESOR';
  return (
    <span
      className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
        necesitaAccion ? 'text-red-700 bg-red-100' : 'text-amber-700 bg-amber-100'
      }`}
    >
      {necesitaAccion ? 'Rechazado' : 'Respuesta enviada'}
    </span>
  );
}

// ── NEW SUB-COMPONENT: GROUPED TERNA STEP CORRECTION FORM ──

interface TernaStepCorrectionFormProps {
  step: MatrizStep;
  nit: string;
  asesoriaId: number;
  ternas: any[]; // full list needed to compute visibleRows
  refetch: () => void | Promise<void>;
  onResponded?: () => void;
  setCampoReq: any;
  columnas: any[];
  reqMap: Map<string, any>;
  calMap: Map<string, string>;
  onSaveSuccess?: () => void;
  historialPorElemento: Map<string, RevisionEntry[]>;
  cambioFieldLogs?: import('@/types/evaluacionAdmin').CambioFieldLog[];
  permiteSubirEvidenciasNuevas?: boolean | null;
  showSecretPanel?: boolean;
}

function TernaStepCorrectionForm({
  step,
  nit,
  asesoriaId,
  ternas,
  refetch,
  onResponded,
  setCampoReq,
  columnas,
  reqMap,
  calMap,
  onSaveSuccess,
  historialPorElemento,
  cambioFieldLogs = [],
  permiteSubirEvidenciasNuevas,
  showSecretPanel,
}: TernaStepCorrectionFormProps) {
  // Initialize requirement ratings map
  console.log({ columnas });
  const initialRatings = useMemo(() => {
    const ratingsMap: Record<string, string> = {};
    const reqIds = [step.ternaDef.requisitoC1Id, step.ternaDef.requisitoC2Id, step.ternaDef.requisitoC3Id].filter(Boolean);
    reqIds.forEach((uuid) => {
      const req = reqMap.get(uuid);
      if (req) {
        ratingsMap[req.id] = req.calificacion || 'NA';
      }
    });
    return ratingsMap;
  }, [step, reqMap]);

  const [ratings, setRatings] = useState<Record<string, string>>(initialRatings);

  // Initialize Terna fields
  const [observaciones, setObservaciones] = useState(step.evalTerna?.observaciones || '');
  const [planMejora, setPlanMejora] = useState(step.evalTerna?.planMejora || '');
  const [fechaImplementacion, setFechaImplementacion] = useState(() => {
    const raw = step.evalTerna?.fechaImplementacion;
    if (!raw) return '';
    try {
      return new Date(raw).toISOString().split('T')[0];
    } catch {
      return '';
    }
  });

  const [saving, setSaving] = useState(false);

  // ── Modal de conversación por requisito/campo (reemplaza ApelacionSection) ──
  const [conversacionAbierta, setConversacionAbierta] = useState<ElementoRechazado | null>(null);

  const entriesDe = (elementoTipo: string, elementoId: string) => historialPorElemento?.get(historialKey(elementoTipo, elementoId)) ?? [];

  const evalTernaId = step.evalTerna?.id;

  const rejectionObs = step.rejectedElements.find((el) => el.elementoTipo === 'OBSERVACIONES');
  const rejectionPlan = step.rejectedElements.find((el) => el.elementoTipo === 'PLANMEJORA');
  const rejectionFecha = step.rejectedElements.find((el) => el.elementoTipo === 'FECHAIMPLEMENTACION');

  const isCampoAprobado = (tipo: string, rejection?: ElementoRechazado) => {
    const targetId = rejection ? rejection.elementoId : evalTernaId;
    if (!targetId) return false;
    const entries = entriesDe(tipo, String(targetId));
    if (entries.length === 0) return false;
    return entries[entries.length - 1].estado === 'APROBADO';
  };

  const getFieldWrapperClass = (tipo: string, rejection?: ElementoRechazado) => {
    if (isCampoAprobado(tipo, rejection)) {
      return 'border-green-200 bg-green-50/5';
    }
    if (rejection) {
      const key = historialKey(rejection.elementoTipo, rejection.elementoId);
      const entries = historialPorElemento?.get(key) ?? [];
      if (estaResuelto(entries)) {
        return 'border-blue-200 bg-blue-50/5';
      }
      return 'border-red-200 bg-red-50/5';
    }
    return 'border-gray-200 bg-white';
  };

  const obsLogAnterior = useMemo(() => {
    const targetId = rejectionObs ? rejectionObs.elementoId : evalTernaId;
    if (!targetId || !cambioFieldLogs) return null;
    return [...cambioFieldLogs]
      .reverse()
      .find((log) => log.entidad === 'TERNA' && String(log.entidadId) === String(targetId) && log.campo === 'observaciones');
  }, [cambioFieldLogs, evalTernaId, rejectionObs]);

  const planLogAnterior = useMemo(() => {
    const targetId = rejectionPlan ? rejectionPlan.elementoId : evalTernaId;
    if (!targetId || !cambioFieldLogs) return null;
    return [...cambioFieldLogs]
      .reverse()
      .find((log) => log.entidad === 'TERNA' && String(log.entidadId) === String(targetId) && log.campo === 'planMejora');
  }, [cambioFieldLogs, evalTernaId, rejectionPlan]);

  const fechaLogAnterior = useMemo(() => {
    const targetId = rejectionFecha ? rejectionFecha.elementoId : evalTernaId;
    if (!targetId || !cambioFieldLogs) return null;
    return [...cambioFieldLogs]
      .reverse()
      .find((log) => log.entidad === 'TERNA' && String(log.entidadId) === String(targetId) && log.campo === 'fechaImplementacion');
  }, [cambioFieldLogs, evalTernaId, rejectionFecha]);

  const obsEditado = !!obsLogAnterior && observaciones !== (obsLogAnterior.valorNuevo ?? '');
  const planEditado = !!planLogAnterior && planMejora !== (planLogAnterior.valorNuevo ?? '');

  const formatDate = (val: string | null) => {
    if (!val) return '—';
    try {
      const d = new Date(val);
      if (isNaN(d.getTime())) return val;
      return d.toLocaleDateString('es-CO', { timeZone: 'America/Bogota', year: 'numeric', month: '2-digit', day: '2-digit' });
    } catch {
      return val;
    }
  };

  const responderConversacion = async (texto: string) => {
    if (!conversacionAbierta) return;
    await asesoriasService.responderRevision(nit, asesoriaId, {
      elementoTipo: conversacionAbierta.elementoTipo,
      elementoId: conversacionAbierta.elementoId,
      respuesta: texto,
    });
    onResponded?.();
    await refetch();
  };

  const editarConversacion = async (texto: string) => {
    if (!conversacionAbierta) return;
    await asesoriasService.editarRespuesta(nit, asesoriaId, {
      elementoTipo: conversacionAbierta.elementoTipo,
      elementoId: conversacionAbierta.elementoId,
      respuesta: texto,
    });
    onResponded?.();
    await refetch();
  };

  // Reset local state whenever step.key or the server data changes (after refetch or navigation)
  useEffect(() => {
    const newRatings: Record<string, string> = {};
    [step.ternaDef.requisitoC1Id, step.ternaDef.requisitoC2Id, step.ternaDef.requisitoC3Id].filter(Boolean).forEach((uuid) => {
      const req = reqMap.get(uuid);
      if (req) newRatings[req.id] = req.calificacion || 'NA';
    });
    setRatings(newRatings);
    setObservaciones(step.evalTerna?.observaciones || '');
    setPlanMejora(step.evalTerna?.planMejora || '');
    const raw = step.evalTerna?.fechaImplementacion;
    setFechaImplementacion(
      raw
        ? (() => {
            try {
              return new Date(raw).toISOString().split('T')[0];
            } catch {
              return '';
            }
          })()
        : ''
    );
  }, [step.key, step.evalTerna, reqMap]);

  // Calculate hasUnsaved to enable Save button
  const hasUnsaved = useMemo(() => {
    const ratingsChanged = Object.entries(ratings).some(([reqId, val]) => val !== initialRatings[reqId]);
    const obsChanged = observaciones !== (step.evalTerna?.observaciones || '');
    const planChanged = planMejora !== (step.evalTerna?.planMejora || '');

    const rawFecha = step.evalTerna?.fechaImplementacion;
    const formattedFecha = rawFecha ? new Date(rawFecha).toISOString().split('T')[0] : '';
    const fechaChanged = fechaImplementacion !== formattedFecha;

    return ratingsChanged || obsChanged || planChanged || fechaChanged;
  }, [ratings, initialRatings, observaciones, planMejora, fechaImplementacion, step.evalTerna]);

  const handleSaveTerna = async () => {
    setSaving(true);
    try {
      // 1. Gather and save rating changes
      const cambiosReqs: Record<string, { calificacion: any }> = {};
      Object.entries(ratings).forEach(([reqId, val]) => {
        if (val !== initialRatings[reqId]) {
          cambiosReqs[reqId] = { calificacion: val as any };
        }
      });

      if (Object.keys(cambiosReqs).length > 0) {
        await requisitosService.batchUpdate({
          nit,
          asesoriaId,
          cambios: cambiosReqs,
        });
        Object.entries(cambiosReqs).forEach(([reqId, change]) => {
          setCampoReq(reqId, 'calificacion', change.calificacion);
        });
      }

      // 2. Save terna fields only if terna fields changed
      const obsChanged = observaciones !== (step.evalTerna?.observaciones || '');
      const planChanged = planMejora !== (step.evalTerna?.planMejora || '');
      const rawFecha = step.evalTerna?.fechaImplementacion;
      const formattedFecha = rawFecha ? new Date(rawFecha).toISOString().split('T')[0] : '';
      const fechaChanged = fechaImplementacion !== formattedFecha;

      const ternaId = step.evalTerna?.ternaId || useEvaluacionTernaStore.getState().evalKeyToTernaId[step.key];
      if (ternaId && (obsChanged || planChanged || fechaChanged)) {
        const payloadTerna = {
          ternaId,
          observaciones,
          planMejora,
          fechaImplementacion: fechaImplementacion || null,
        };
        await requisitosService.batchUpdateTernas(nit, [payloadTerna], asesoriaId);
      }

      // 3. Mark rejected elements as resolved
      const itemsParaMarcar = step.rejectedElements.map((el) => ({
        elementoTipo: el.elementoTipo,
        elementoId: el.elementoId,
      }));
      if (itemsParaMarcar.length > 0) {
        await asesoriasService.marcarCorregido(nit, asesoriaId, itemsParaMarcar);
      }

      await refetch();
      onResponded?.();
      onSaveSuccess?.();
    } catch (err) {
      console.error('Error saving terna corrections:', err);
      alert('Error al guardar los cambios en la terna');
    } finally {
      setSaving(false);
    }
  };

  // ── visibleRows: same logic as TernaRevisionStepper ──────────────────────
  const [expanded, setExpanded] = useState(false);

  const defaultRows: any[] = useMemo(() => {
    const td = step.ternaDef;
    if (!td) return [];
    return td.grupo ? ternas.filter((t: any) => t.grupo === td.grupo) : ternas.filter((t: any) => t.id === td.id);
  }, [step.ternaDef, ternas]);

  const allRowsForOrden: any[] = useMemo(() => {
    const first = defaultRows[0];
    if (!first) return defaultRows;
    return ternas.filter((t: any) => t.orden === first.orden);
  }, [defaultRows, ternas]);

  const visibleRows = expanded ? allRowsForOrden : defaultRows;
  const canExpand = allRowsForOrden.length > defaultRows.length;

  return (
    <div className="space-y-6">
      {/* ── Full terna matrix (same layout as TernaRevisionStepper) ── */}
      <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
        {/* Header: NTC column names */}
        <div className="flex bg-gray-50/80 text-[9px] font-bold uppercase tracking-wide text-gray-400">
          <div className="w-1.5 shrink-0" />
          {columnas.map((col: any) => (
            <div key={col.ntcId} className="flex-1 min-w-0 px-2 py-2 border-r border-gray-100 truncate">
              {col.nombre}
            </div>
          ))}
        </div>

        {/* Data rows */}
        <div className="flex flex-col">
          {visibleRows.map((ternaDef: any) => (
            <div key={ternaDef.id} className="flex items-stretch border-b border-gray-50 last:border-b-0">
              {/* blue dot for rows in defaultRows scope */}
              <div className="w-1.5 shrink-0 flex items-center justify-center py-3">
                {defaultRows.some((r: any) => r.id === ternaDef.id) && <span className="w-1.5 h-1.5 rounded-full bg-blue-400 block" />}
              </div>
              {/* NTC cells */}
              {columnas.map((col: any) => {
                const reqUuid =
                  col.orden === 1
                    ? ternaDef.requisitoC1Id
                    : col.orden === 2
                      ? ternaDef.requisitoC2Id
                      : col.orden === 3
                        ? ternaDef.requisitoC3Id
                        : null;
                const req = reqUuid ? reqMap.get(reqUuid) : null;
                console.log({ req });
                const currentCal = req ? ratings[req.id] || calMap.get(req.id) : null;

                // Check if this specific req is rejected
                const reqIdInEval = req?.evaluacionRequisito?.id;
                const rejection = req
                  ? step.rejectedElements.find((el) => el.elementoTipo === 'REQUISITO' && Number(el.elementoId) === reqIdInEval)
                  : null;

                return (
                  <div
                    key={col.ntcId}
                    className={`flex-1 min-w-0 px-2 py-2 border-r border-gray-100 last:border-r-0 flex flex-col gap-1 ${
                      rejection ? 'bg-red-50/30' : ''
                    }`}
                  >
                    {req ? (
                      <>
                        {/* calificacion badge + numeral */}
                        <div className="flex items-center gap-1 flex-wrap">
                          {currentCal ? (
                            <span
                              className={`text-[9px] font-bold border px-1.5 py-0.5 rounded-full leading-none ${
                                CAL_STYLES[currentCal] ?? CAL_STYLES['NA']
                              }`}
                            >
                              {currentCal}
                            </span>
                          ) : null}
                          <span className="text-[10px] font-semibold text-gray-500">{req.numeral}</span>
                          {showSecretPanel && (
                            <span className="font-mono text-[9px] text-amber-700 bg-amber-50 px-1 py-0.5 rounded border border-amber-200 select-all font-semibold">
                              #{req.evaluacionRequisito?.id ?? req.id}
                            </span>
                          )}
                          {rejection && <EstadoRechazoBadge entries={entriesDe(rejection.elementoTipo, rejection.elementoId)} />}
                        </div>

                        {/* descripcion */}
                        <p className="text-[10px] text-gray-400 leading-relaxed whitespace-pre-wrap line-clamp-3 hover:line-clamp-none transition-all">
                          {req.descripcion}
                        </p>

                        {/* admin rejection comment */}
                        {rejection && rejection.observacion && (
                          <div className="mt-1 bg-red-50 border border-red-100 rounded px-2 py-1.5 text-[10px] text-red-800">
                            <span className="font-semibold text-red-700">Administrador:</span> "{rejection.observacion}"
                          </div>
                        )}

                        {/* Rating selector (only for rejected reqs or when row is in scope) */}
                        {defaultRows.some((r: any) => r.id === ternaDef.id) && (
                          <div className="flex items-center gap-1 mt-1">
                            {(['C', 'NC', 'CP', 'NA'] as const).map((c) => {
                              const isSelected = ratings[req.id] === c;
                              return (
                                <button
                                  key={c}
                                  type="button"
                                  onClick={() => setRatings((prev) => ({ ...prev, [req.id]: c }))}
                                  className={`px-1.5 py-0.5 border rounded text-[9px] font-bold transition-all leading-none ${
                                    isSelected
                                      ? CAL_STYLES[c]
                                      : 'bg-white text-gray-400 border-gray-200 hover:text-gray-600 hover:bg-gray-50'
                                  }`}
                                >
                                  {c}
                                </button>
                              );
                            })}
                          </div>
                        )}

                        {rejection && (
                          <button
                            type="button"
                            data-testid="cc-ver-conversacion"
                            onClick={() => setConversacionAbierta(rejection)}
                            className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-gray-500 hover:text-gray-700"
                          >
                            <EstadoDot estado={estadoConversacion(entriesDe(rejection.elementoTipo, rejection.elementoId))} />
                            <MessageCircle className="w-3 h-3" />
                            Ver conversación
                            <PendingBadge count={entriesDe(rejection.elementoTipo, rejection.elementoId).length} />
                          </button>
                        )}
                      </>
                    ) : (
                      <span className="text-gray-300 text-[10px] italic">—</span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Expand/collapse other rows of same order */}
        {canExpand && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="w-full py-1.5 text-center text-[10px] text-gray-400 hover:text-gray-600 hover:bg-gray-50 border-t border-gray-100 transition-colors"
          >
            {expanded
              ? `▲ Mostrar solo la terna seleccionada`
              : `▼ Ver ${allRowsForOrden.length - defaultRows.length} fila(s) más del mismo orden`}
          </button>
        )}
      </div>

      {/* Editable Terna Fields */}
      <div className="space-y-4">
        <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400">Campos de la Terna</h4>

        {/* Observaciones Field */}
        {(() => {
          const rejection = step.rejectedElements.find((el) => el.elementoTipo === 'OBSERVACIONES');
          return (
            <div className={`p-4 border rounded-xl shadow-sm space-y-3 ${getFieldWrapperClass('OBSERVACIONES', rejection)}`}>
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-bold text-gray-700">
                  Observaciones
                  {showSecretPanel && rejection && (
                    <span className="font-mono text-[10px] text-amber-700 bg-amber-50 px-1 py-0.5 rounded border border-amber-200 select-all font-semibold ml-1.5">
                      #{rejection.elementoId}
                    </span>
                  )}
                </span>
                {rejection && <EstadoRechazoBadge entries={entriesDe(rejection.elementoTipo, rejection.elementoId)} />}
              </div>
              {isCampoAprobado('OBSERVACIONES', rejection) && (
                <div className="text-[11px] text-green-700 bg-green-50 border border-green-200 rounded px-2.5 py-1.5 font-semibold">
                  ✓ Aprobado por administrador
                </div>
              )}
              {rejection && (
                <p
                  className={`text-[11px] italic rounded px-2.5 py-1.5 ${
                    isCampoAprobado('OBSERVACIONES', rejection)
                      ? 'text-gray-600 bg-gray-50 border border-gray-200'
                      : 'text-red-800 bg-red-50 border border-red-100'
                  }`}
                >
                  <span className="font-semibold">Comentario del administrador:</span> "{rejection.observacion || 'sin comentario'}"
                </p>
              )}
              {obsLogAnterior && (
                <div className="text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1.5 flex items-start gap-1.5">
                  <History className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gray-400" />
                  <span>
                    <span className="font-semibold">Versión anterior enviada:</span>{' '}
                    <span className="italic whitespace-pre-wrap">{obsLogAnterior.valorNuevo || '—'}</span>
                  </span>
                </div>
              )}
              <textarea
                rows={3}
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
                placeholder="Escribe las observaciones..."
                disabled={!rejection || isCampoAprobado('OBSERVACIONES', rejection)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs bg-white text-gray-800 focus:ring-1 focus:ring-gray-400 focus:outline-none disabled:bg-gray-100 disabled:text-gray-500"
              />
              {obsEditado && (
                <p className="text-[11px] text-amber-600 font-medium">Editado — guarda los cambios para aplicar la corrección.</p>
              )}
              {rejection && (
                <button
                  type="button"
                  data-testid="cc-ver-conversacion"
                  onClick={() => setConversacionAbierta(rejection)}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-500 hover:text-gray-700"
                >
                  <EstadoDot estado={estadoConversacion(entriesDe(rejection.elementoTipo, rejection.elementoId))} />
                  <MessageCircle className="w-3.5 h-3.5" />
                  Ver conversación
                  <PendingBadge count={entriesDe(rejection.elementoTipo, rejection.elementoId).length} />
                </button>
              )}
            </div>
          );
        })()}

        {/* Plan de Mejora Field */}
        {(() => {
          const rejection = step.rejectedElements.find((el) => el.elementoTipo === 'PLANMEJORA');
          return (
            <div className={`p-4 border rounded-xl shadow-sm space-y-3 ${getFieldWrapperClass('PLANMEJORA', rejection)}`}>
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-bold text-gray-700">
                  Plan de Mejora
                  {showSecretPanel && rejection && (
                    <span className="font-mono text-[10px] text-amber-700 bg-amber-50 px-1 py-0.5 rounded border border-amber-200 select-all font-semibold ml-1.5">
                      #{rejection.elementoId}
                    </span>
                  )}
                </span>
                {rejection && <EstadoRechazoBadge entries={entriesDe(rejection.elementoTipo, rejection.elementoId)} />}
              </div>
              {isCampoAprobado('PLANMEJORA', rejection) && (
                <div className="text-[11px] text-green-700 bg-green-50 border border-green-200 rounded px-2.5 py-1.5 font-semibold">
                  ✓ Aprobado por administrador
                </div>
              )}
              {rejection && (
                <p
                  className={`text-[11px] italic rounded px-2.5 py-1.5 ${
                    isCampoAprobado('PLANMEJORA', rejection)
                      ? 'text-gray-600 bg-gray-50 border border-gray-200'
                      : 'text-red-800 bg-red-50 border border-red-100'
                  }`}
                >
                  <span className="font-semibold">Comentario del administrador:</span> "{rejection.observacion || 'sin comentario'}"
                </p>
              )}
              {planLogAnterior && (
                <div className="text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1.5 flex items-start gap-1.5">
                  <History className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gray-400" />
                  <span>
                    <span className="font-semibold">Versión anterior enviada:</span>{' '}
                    <span className="italic whitespace-pre-wrap">{planLogAnterior.valorNuevo || '—'}</span>
                  </span>
                </div>
              )}
              <textarea
                rows={3}
                value={planMejora}
                onChange={(e) => setPlanMejora(e.target.value)}
                placeholder="Escribe el plan de mejora..."
                disabled={!rejection || isCampoAprobado('PLANMEJORA', rejection)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs bg-white text-gray-800 focus:ring-1 focus:ring-gray-400 focus:outline-none disabled:bg-gray-100 disabled:text-gray-500"
              />
              {planEditado && (
                <p className="text-[11px] text-amber-600 font-medium">Editado — guarda los cambios para aplicar la corrección.</p>
              )}
              {rejection && (
                <button
                  type="button"
                  data-testid="cc-ver-conversacion"
                  onClick={() => setConversacionAbierta(rejection)}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-500 hover:text-gray-700"
                >
                  <EstadoDot estado={estadoConversacion(entriesDe(rejection.elementoTipo, rejection.elementoId))} />
                  <MessageCircle className="w-3.5 h-3.5" />
                  Ver conversación
                  <PendingBadge count={entriesDe(rejection.elementoTipo, rejection.elementoId).length} />
                </button>
              )}
            </div>
          );
        })()}

        {/* Fecha de Implementación Field */}
        {(() => {
          const rejection = step.rejectedElements.find((el) => el.elementoTipo === 'FECHAIMPLEMENTACION');
          return (
            <div className={`p-4 border rounded-xl shadow-sm space-y-3 ${getFieldWrapperClass('FECHAIMPLEMENTACION', rejection)}`}>
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-bold text-gray-700">
                  Fecha de Implementación
                  {showSecretPanel && rejection && (
                    <span className="font-mono text-[10px] text-amber-700 bg-amber-50 px-1 py-0.5 rounded border border-amber-200 select-all font-semibold ml-1.5">
                      #{rejection.elementoId}
                    </span>
                  )}
                </span>
                {rejection && <EstadoRechazoBadge entries={entriesDe(rejection.elementoTipo, rejection.elementoId)} />}
              </div>
              {isCampoAprobado('FECHAIMPLEMENTACION', rejection) && (
                <div className="text-[11px] text-green-700 bg-green-50 border border-green-200 rounded px-2.5 py-1.5 font-semibold">
                  ✓ Aprobado por administrador
                </div>
              )}
              {rejection && (
                <p
                  className={`text-[11px] italic rounded px-2.5 py-1.5 ${
                    isCampoAprobado('FECHAIMPLEMENTACION', rejection)
                      ? 'text-gray-600 bg-gray-50 border border-gray-200'
                      : 'text-red-800 bg-red-50 border border-red-100'
                  }`}
                >
                  <span className="font-semibold">Comentario del administrador:</span> "{rejection.observacion || 'sin comentario'}"
                </p>
              )}
              {fechaLogAnterior && (
                <div className="text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1.5 flex items-start gap-1.5">
                  <History className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gray-400" />
                  <span>
                    <span className="font-semibold">Versión anterior enviada:</span>{' '}
                    <span className="italic">{formatDate(fechaLogAnterior.valorNuevo)}</span>
                  </span>
                </div>
              )}
              <input
                type="date"
                value={fechaImplementacion}
                onChange={(e) => setFechaImplementacion(e.target.value)}
                disabled={!rejection || isCampoAprobado('FECHAIMPLEMENTACION', rejection)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs bg-white text-gray-800 focus:ring-1 focus:ring-gray-400 focus:outline-none disabled:bg-gray-100 disabled:text-gray-500"
              />
              {(() => {
                const getFormattedLogFecha = (val: string | null | undefined) => {
                  if (!val) return '';
                  try {
                    return new Date(val).toISOString().split('T')[0];
                  } catch {
                    return '';
                  }
                };
                const logFechaFormatted = fechaLogAnterior ? getFormattedLogFecha(fechaLogAnterior.valorNuevo) : '';
                const fechaEditado = !!fechaLogAnterior && fechaImplementacion !== logFechaFormatted;
                return (
                  fechaEditado && (
                    <p className="text-[11px] text-amber-600 font-medium mt-1">Editado — guarda los cambios para aplicar la corrección.</p>
                  )
                );
              })()}
              {rejection && (
                <button
                  type="button"
                  data-testid="cc-ver-conversacion"
                  onClick={() => setConversacionAbierta(rejection)}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-500 hover:text-gray-700"
                >
                  <EstadoDot estado={estadoConversacion(entriesDe(rejection.elementoTipo, rejection.elementoId))} />
                  <MessageCircle className="w-3.5 h-3.5" />
                  Ver conversación
                  <PendingBadge count={entriesDe(rejection.elementoTipo, rejection.elementoId).length} />
                </button>
              )}
            </div>
          );
        })()}
      </div>

      {/* Modal de conversación por requisito/campo (§6.4) */}
      {conversacionAbierta && (
        <Modal
          isOpen
          onClose={() => setConversacionAbierta(null)}
          title={conversacionAbierta.elementoNombre || LABEL_TIPO[conversacionAbierta.elementoTipo] || 'Conversación'}
        >
          <CorreccionesChat
            entries={entriesDe(conversacionAbierta.elementoTipo, conversacionAbierta.elementoId)}
            actorActual="asesor"
            canReply={puedeComentarAsesor(entriesDe(conversacionAbierta.elementoTipo, conversacionAbierta.elementoId))}
            comentariosRestantes={comentariosRestantesRonda(entriesDe(conversacionAbierta.elementoTipo, conversacionAbierta.elementoId))}
            onReply={responderConversacion}
            onEditReply={editarConversacion}
            permiteSubirEvidenciasNuevas={permiteSubirEvidenciasNuevas}
          />
        </Modal>
      )}

      {/* Save Step Footer */}
      <div className="flex items-center justify-between border-t border-gray-150 pt-4 mt-6">
        <div className="text-xs text-gray-400">
          {hasUnsaved ? (
            <i className="text-amber-500 not-italic font-semibold">* Cambios sin guardar</i>
          ) : (
            <span className="text-green-600 font-medium">Todo guardado</span>
          )}
        </div>
        <button
          type="button"
          onClick={handleSaveTerna}
          disabled={saving || !hasUnsaved}
          className="flex items-center gap-1.5 px-4 py-2 text-xs bg-gray-900 text-white rounded-lg font-bold hover:bg-gray-800 disabled:opacity-40 transition-colors"
        >
          <Save className="w-3.5 h-3.5" />
          {saving ? 'Guardando...' : 'Guardar paso'}
        </button>
      </div>
    </div>
  );
}

// ── SUB-COMPONENTS FOR EVIDENCIAS / AUDIOS TABS ──

function EvidenciaCorrectionForm({
  el,
  nit,
  evidencias,
  cadenasEvidencias,
  setIsModalOpen,
  setPrefillNombre,
  setReemplazaId,
  onEliminarEvidencia,
  historialPorElemento,
}: {
  el: ElementoRechazado;
  nit: string;
  evidencias: any[];
  cadenasEvidencias: ReturnType<typeof agruparCadenaArchivos<any>>;
  setIsModalOpen: any;
  setPrefillNombre: any;
  setReemplazaId: any;
  onEliminarEvidencia?: (evidenciaId: number) => Promise<void>;
  historialPorElemento?: Map<string, RevisionEntry[]>;
}) {
  const auth = useOptionalAuth();
  const user = auth?.user;
  const evId = Number(el.elementoId);
  const evidenciaRechazada = evidencias.find((ev) => ev.evidenciaId === evId);
  const cadena = cadenasEvidencias.find((c) => c.version.some((v: any) => v.evidenciaId === evId));
  const idParaReemplazar = cadena ? cadena.actual.evidenciaId : evId;
  const evidenciaVigente = cadena ? cadena.actual : evidenciaRechazada;

  const entriesRechazo = historialPorElemento?.get(historialKey('EVIDENCIA', evId)) ?? [];
  const isEvidenciaAprobada = evidenciaVigente?.estado === 'APROBADO' || estaAprobado(entriesRechazo);

  const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);
  const [accesoEmail, setAccesoEmail] = useState('');
  const [solicitandoAcceso, setSolicitandoAcceso] = useState(false);
  const [accesoExito, setAccesoExito] = useState<string | null>(null);
  const [accesoError, setAccesoError] = useState<string | null>(null);

  const handleResubir = () => {
    setPrefillNombre(evidenciaRechazada?.nombre);
    setReemplazaId(idParaReemplazar);
    setIsModalOpen(true);
  };

  const abrirAccesoModal = () => {
    setAccesoEmail('');
    setAccesoExito(null);
    setAccesoError(null);
    setIsAccessModalOpen(true);
  };

  const cerrarAccesoModal = () => {
    if (!solicitandoAcceso) {
      setIsAccessModalOpen(false);
    }
  };

  const handleEnviarSolicitud = async () => {
    if (!evidenciaVigente?.evidenciaId) return;
    setSolicitandoAcceso(true);
    setAccesoError(null);
    setAccesoExito(null);
    try {
      const res = await evidenciasService.solicitarAcceso(evidenciaVigente.evidenciaId, accesoEmail.trim() || user?.email || undefined);
      setAccesoExito(res.message || 'Acceso solicitado con éxito');
    } catch (e) {
      setAccesoError(e instanceof Error ? e.message : 'Error al solicitar acceso');
    } finally {
      setSolicitandoAcceso(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <span className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-gray-900 text-white">EVIDENCIA</span>
          <div className="flex items-center gap-2">
            {evidenciaVigente?.url && (
              <a
                href={evidenciaVigente.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[12px] font-medium text-blue-600 hover:underline"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Abrir
              </a>
            )}
            {evidenciaVigente?.evidenciaId && (
              <button
                type="button"
                onClick={abrirAccesoModal}
                className="inline-flex items-center gap-1 text-[12px] font-medium text-blue-600 hover:underline"
              >
                <Key className="w-3.5 h-3.5" />
                Obtener acceso
              </button>
            )}
            {onEliminarEvidencia && evidenciaVigente?.evidenciaId && !isEvidenciaAprobada && (
              <button
                type="button"
                onClick={() => onEliminarEvidencia(evidenciaVigente.evidenciaId)}
                className="inline-flex items-center gap-1 text-[12px] font-medium text-red-600 hover:underline cursor-pointer"
                title="Eliminar evidencia"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Eliminar
              </button>
            )}
          </div>
        </div>

        <div>
          <div className="relative group inline-block max-w-full">
            <h4 className="text-[14px] font-bold text-gray-900 truncate">
              {evidenciaVigente?.nombre || evidenciaRechazada?.nombre || 'Archivo'}
            </h4>
            <div className="absolute left-0 bottom-full mb-1.5 hidden group-hover:block z-50 whitespace-nowrap bg-gray-900 text-white text-[10px] px-2 py-1 rounded shadow-lg">
              Este es el último documento de esta evidencia
            </div>
          </div>
          {evidenciaRechazada?.descripcion && <p className="text-[11px] text-gray-500 mt-1">{evidenciaRechazada.descripcion}</p>}
        </div>
      </div>

      <Modal isOpen={isAccessModalOpen} onClose={cerrarAccesoModal} title="Obtener acceso al archivo">
        <div className="space-y-4">
          {accesoExito ? (
            <>
              <p className="text-[13px] text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2">{accesoExito}</p>
              <div className="flex justify-end mt-4">
                <button type="button" onClick={cerrarAccesoModal} className="admin-btn primary">
                  Cerrar
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-[13px]" style={{ color: 'var(--admin-text2)' }}>
                Ingresa el correo al que quieres otorgar acceso de lectura en Drive. Si lo dejas vacío, se usará el correo de tu cuenta.
              </p>
              <Input
                label="Correo electrónico (opcional)"
                type="email"
                placeholder={user?.email ?? 'tu correo'}
                value={accesoEmail}
                onChange={(e) => {
                  setAccesoEmail(e.target.value);
                  setAccesoError(null);
                }}
                disabled={solicitandoAcceso}
              />
              {accesoError && (
                <p className="text-[12px] text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{accesoError}</p>
              )}
              <div className="flex justify-end gap-2 mt-4">
                <button type="button" onClick={cerrarAccesoModal} disabled={solicitandoAcceso} className="admin-btn disabled:opacity-50">
                  Cancelar
                </button>
                <button
                  type="button"
                  disabled={solicitandoAcceso}
                  className="admin-btn primary disabled:opacity-50"
                  onClick={handleEnviarSolicitud}
                >
                  {solicitandoAcceso ? 'Solicitando...' : 'Solicitar acceso'}
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      <div className="space-y-2">
        <p className="text-[12px] font-semibold text-gray-700">Sube una nueva versión del archivo:</p>
        <button type="button" onClick={handleResubir} className="admin-btn primary text-[12px] flex items-center gap-1.5">
          <Upload className="w-4 h-4" /> Re-subir versión corregida
        </button>
      </div>
    </div>
  );
}

function AudioCorrectionForm({
  el,
  nit,
  asesoriaId,
  audios,
  refetch,
  onResponded,
  historialPorElemento,
  onEliminarAudio,
}: {
  el: ElementoRechazado;
  nit: string;
  asesoriaId: number;
  audios: any[];
  refetch: () => void | Promise<void>;
  onResponded?: () => void;
  historialPorElemento: Map<string, RevisionEntry[]>;
  onEliminarAudio?: (audioId: number) => Promise<void>;
}) {
  const auth = useOptionalAuth();
  const user = auth?.user;
  const audioId = Number(el.elementoId);
  const audioRechazado = audios.find((a) => a.audioId === audioId);
  const reemplazo = audios.find((a) => a.reemplazaAudioId === audioId);
  const audioVigente = reemplazo || audioRechazado;

  const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);
  const [accesoEmail, setAccesoEmail] = useState('');
  const [solicitandoAcceso, setSolicitandoAcceso] = useState(false);
  const [accesoExito, setAccesoExito] = useState<string | null>(null);
  const [accesoError, setAccesoError] = useState<string | null>(null);

  const entriesRechazo = historialPorElemento.get(historialKey('AUDIO', audioId)) ?? [];
  const isAudioAprobado = audioVigente?.estado === 'APROBADO' || estaAprobado(entriesRechazo);
  const fechaRechazo = entriesRechazo
    .filter((e) => e.estado === 'RECHAZADO')
    .reduce((max, e) => Math.max(max, new Date(e.fecha).getTime()), -Infinity);

  const audiosDisponibles = audios.filter(
    (a) =>
      a.estado !== 'RECHAZADO' &&
      a.audioId !== audioId &&
      a.audioId !== reemplazo?.audioId &&
      a.reemplazaAudioId == null &&
      (fechaRechazo === -Infinity || new Date(a.fechaGrabacion).getTime() > fechaRechazo)
  );
  const [linkingBusy, setLinkingBusy] = useState(false);

  const handleVincular = async (newReemplazoId: number | null) => {
    setLinkingBusy(true);
    try {
      await asesoriasService.vincularAudioReemplazo(
        nit,
        asesoriaId,
        newReemplazoId ?? (reemplazo?.audioId || 0),
        newReemplazoId ? audioId : null
      );

      await refetch();
      onResponded?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error al vincular el audio');
    } finally {
      setLinkingBusy(false);
    }
  };

  const abrirAccesoModal = () => {
    setAccesoEmail('');
    setAccesoExito(null);
    setAccesoError(null);
    setIsAccessModalOpen(true);
  };

  const cerrarAccesoModal = () => {
    if (!solicitandoAcceso) {
      setIsAccessModalOpen(false);
    }
  };

  const handleEnviarSolicitud = async () => {
    if (!audioVigente?.audioId) return;
    setSolicitandoAcceso(true);
    setAccesoError(null);
    setAccesoExito(null);
    try {
      const res = await audiosService.requestAudioAccess(nit, audioVigente.audioId, accesoEmail.trim() || user?.email || undefined);
      setAccesoExito(res.message || 'Acceso solicitado con éxito');
    } catch (e) {
      setAccesoError(e instanceof Error ? e.message : 'Error al solicitar acceso');
    } finally {
      setSolicitandoAcceso(false);
    }
  };

  const fechaAudioRaw =
    audioRechazado?.fechaGrabacion ?? (fechaRechazo !== -Infinity ? new Date(fechaRechazo) : null) ?? (el as any).fechaGrabacion;
  const fechaAudioLabel = fechaAudioRaw ? new Date(fechaAudioRaw).toLocaleDateString() : 'Fecha desconocida';

  const audioGoogleId =
    audioVigente?.googleId || audioVigente?.adjunteDelAudio || audioRechazado?.googleId || audioRechazado?.adjunteDelAudio;
  const audioLink = audioGoogleId ? `https://drive.google.com/file/d/${audioGoogleId}/view` : undefined;

  return (
    <div className="space-y-4">
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <span className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-gray-900 text-white">AUDIO</span>
          <div className="flex items-center gap-2">
            {audioLink && (
              <a
                href={audioLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[12px] font-medium text-blue-600 hover:underline"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Abrir
              </a>
            )}
            {audioVigente?.audioId && (
              <button
                type="button"
                onClick={abrirAccesoModal}
                className="inline-flex items-center gap-1 text-[12px] font-medium text-blue-600 hover:underline"
              >
                <Key className="w-3.5 h-3.5" />
                Obtener acceso
              </button>
            )}
            {onEliminarAudio && audioVigente?.audioId && !isAudioAprobado && (
              <button
                type="button"
                onClick={() => onEliminarAudio(audioVigente.audioId)}
                className="inline-flex items-center gap-1 text-[12px] font-medium text-red-600 hover:underline cursor-pointer"
                title="Eliminar audio"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Eliminar
              </button>
            )}
          </div>
        </div>

        <div>
          <div className="relative group inline-block max-w-full">
            <h4 className="text-[14px] font-bold text-gray-900 truncate">Audio grabado el {fechaAudioLabel}</h4>
            <div className="absolute left-0 bottom-full mb-1.5 hidden group-hover:block z-50 whitespace-nowrap bg-gray-900 text-white text-[10px] px-2 py-1 rounded shadow-lg">
              Este es el último documento de esta evidencia
            </div>
          </div>
        </div>
      </div>

      <Modal isOpen={isAccessModalOpen} onClose={cerrarAccesoModal} title="Obtener acceso al archivo">
        <div className="space-y-4">
          {accesoExito ? (
            <>
              <p className="text-[13px] text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2">{accesoExito}</p>
              <div className="flex justify-end mt-4">
                <button type="button" onClick={cerrarAccesoModal} className="admin-btn primary">
                  Cerrar
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-[13px]" style={{ color: 'var(--admin-text2)' }}>
                Ingresa el correo al que quieres otorgar acceso de lectura en Drive. Si lo dejas vacío, se usará el correo de tu cuenta.
              </p>
              <Input
                label="Correo electrónico (opcional)"
                type="email"
                placeholder={user?.email ?? 'tu correo'}
                value={accesoEmail}
                onChange={(e) => {
                  setAccesoEmail(e.target.value);
                  setAccesoError(null);
                }}
                disabled={solicitandoAcceso}
              />
              {accesoError && (
                <p className="text-[12px] text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{accesoError}</p>
              )}
              <div className="flex justify-end gap-2 mt-4">
                <button type="button" onClick={cerrarAccesoModal} disabled={solicitandoAcceso} className="admin-btn disabled:opacity-50">
                  Cancelar
                </button>
                <button
                  type="button"
                  disabled={solicitandoAcceso}
                  className="admin-btn primary disabled:opacity-50"
                  onClick={handleEnviarSolicitud}
                >
                  {solicitandoAcceso ? 'Solicitando...' : 'Solicitar acceso'}
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      <div className="bg-red-50 rounded-lg p-4 border border-red-200 text-red-700">
        <div className="flex gap-2">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-[12px]">Motivo del rechazo:</p>
            <p className="text-[12px] mt-0.5 italic">"{el.observacion || 'sin comentario'}"</p>
          </div>
        </div>
      </div>

      {reemplazo && (
        <div className="bg-[#f0fdf4] border border-[#bbf7d0] rounded-lg p-4">
          <p className="text-[12px] font-bold text-[#166534] flex items-center gap-1.5 mb-2">
            <CheckCircle className="w-4 h-4" /> Ya vinculaste un audio corregido como reemplazo
          </p>
          <div className="flex items-center justify-between bg-white border border-[#bbf7d0] rounded-lg px-3 py-2">
            <div className="flex items-center gap-2 min-w-0">
              <Link2 className="w-4 h-4 text-[#166534] shrink-0" />
              <span className="text-[12px] font-medium text-[#166534] truncate">
                Audio del {new Date(reemplazo.fechaGrabacion).toLocaleDateString()}
              </span>
            </div>
            <button
              type="button"
              disabled={linkingBusy}
              onClick={() => handleVincular(null)}
              className="text-red-500 hover:text-red-700 text-[11px] font-medium"
            >
              Desvincular
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <p className="text-[12px] font-semibold text-gray-700">
          {reemplazo ? 'Reemplazar por otro audio subido desde el formulario:' : 'Vincular con un audio subido desde el formulario:'}
        </p>
        {audiosDisponibles.length === 0 ? (
          <p className="text-[11px] text-gray-500 italic bg-gray-50 border border-gray-200 rounded p-2.5">
            No hay audios nuevos disponibles. Sube el nuevo audio a través de tu Google Form y espera a que se sincronice.
          </p>
        ) : (
          <SearchableSelect
            value=""
            onChange={(value) => value && handleVincular(Number(value))}
            disabled={linkingBusy}
            placeholder="Selecciona el audio de reemplazo..."
            searchPlaceholder="Buscar audio..."
            className="max-w-sm"
            options={audiosDisponibles.map((a) => ({
              value: String(a.audioId),
              label: `Audio del ${new Date(a.fechaGrabacion).toLocaleString()} (${formatDuracionCorta(a.duracionSeg || a.duracion || 0)})`,
            }))}
          />
        )}
      </div>
    </div>
  );
}

// ── MAIN CORE STACK COMPONENT ──

export function CorreccionesStepper({
  elementosRechazados,
  nit,
  asesoriaId,
  historialPorElemento,
  evidencias,
  audios,
  requisitos,
  ternas,
  evaluacionesTernas,
  cambioFieldLogs = [],
  ntcs,
  refetch,
  clavesResueltas = new Set(),
  onResponded,
  permiteSubirEvidenciasNuevas,
  correccionesCtx,
  onEliminarEvidencia,
  onEliminarAudio,
}: Props) {
  const showSecretPanel = useAdminSecretPanel();
  // Sync sub-tab with URL ?subtab= param
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSubTab = (searchParams.get('subtab') ?? 'matriz') as SubTab;
  const setActiveSubTab = (sub: SubTab) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('subtab', sub);
        return next;
      },
      { replace: true }
    );

  const [matrizIndex, setMatrizIndex] = useState(0);
  const [evidenciasIndex, setEvidenciasIndex] = useState(0);
  const [audiosIndex, setAudiosIndex] = useState(0);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reemplazaId, setReemplazaId] = useState<number | undefined>(undefined);
  const [prefillNombre, setPrefillNombre] = useState<string | undefined>(undefined);

  const setCampoReq = useEvaluacionStore((s) => s.setCampo);

  // Cadenas de reemplazo de evidencia/audio (borrado lógico: la fila vieja no se borra, la
  // nueva apunta a ella vía reemplaza*Id) — para adjuntar el archivo real al evento de sistema
  // "se subió una nueva versión" en el chat, igual que en la vista de administrador.
  const cadenasEvidencias = useMemo(
    () =>
      agruparCadenaArchivos(
        evidencias,
        (e: any) => e.evidenciaId,
        (e: any) => e.reemplazaEvidenciaId ?? null
      ),
    [evidencias]
  );
  const cadenasAudios = useMemo(
    () =>
      // A diferencia de evidencias (el backend ya excluye ELIMINADO para el asesor), el audio
      // sí llega con esas filas — hay que filtrarlas nosotros o quedan como "hojas" fantasma que
      // arman una segunda cadena ambigua hacia la misma raíz.
      agruparCadenaArchivos(
        audios.filter((a: any) => a.estado !== 'ELIMINADO'),
        (a: any) => a.audioId,
        (a: any) => a.reemplazaAudioId ?? null
      ),
    [audios]
  );

  // Para el asesor, solo la versión VIGENTE del archivo es accesible — cada reemplazo "olvida"
  // la anterior (deja de tener link, queda como tag). El admin sí conserva acceso histórico
  // completo (ver AdminRevisionDetailPage.tsx, que no pasa por esta función).
  const entriesConArchivo = (elementoTipo: string, elementoId: string): RevisionEntry[] => {
    if (elementoTipo !== 'EVIDENCIA' && elementoTipo !== 'AUDIO') {
      return historialPorElemento?.get(historialKey(elementoTipo, elementoId)) ?? [];
    }
    const esEvidencia = elementoTipo === 'EVIDENCIA';
    // El elemento abierto en el chat es el RECHAZADO original (raíz de la cadena), no el
    // vigente (hoja) — hay que buscar por cualquier versión de la cadena, no solo `actual`.
    const cadena = (esEvidencia ? cadenasEvidencias : cadenasAudios).find((c: any) =>
      c.version.some((v: any) => String(esEvidencia ? v.evidenciaId : v.audioId) === elementoId)
    );
    if (!cadena) return historialPorElemento?.get(historialKey(elementoTipo, elementoId)) ?? [];

    // Posición (1-based) de cada versión dentro de la cadena — para que un comentario indique
    // a cuál se refería, algo imposible de saber a simple vista una vez que hay 3-4 versiones.
    const posicionPorId = new Map<number, number>(
      cadena.version.map((v: any, i: number) => [esEvidencia ? v.evidenciaId : v.audioId, i + 1])
    );

    const entries = cadena.version.flatMap(
      (v: any) => historialPorElemento?.get(historialKey(elementoTipo, esEvidencia ? v.evidenciaId : v.audioId)) ?? []
    );
    const estadoSubida = esEvidencia ? 'SISTEMA_EVIDENCIA_SUBIDA' : 'SISTEMA_AUDIO_VINCULADO';
    const archivoDe = (v: any) =>
      esEvidencia
        ? { nombre: v.nombre, url: v.url, descripcion: v.descripcion ?? null }
        : {
            nombre: `Audio · ${v.asesorNombre ?? 'Asesor'}`,
            url: `https://drive.google.com/file/d/${v.adjunteDelAudio}/view`,
            duracionSeg: v.duracionSeg,
          };

    // Cuando un reemplazo se elimina y se vuelve a subir otro, quedan DOS eventos "se subió una
    // nueva versión" apuntando a la misma raíz (uno huérfano del reemplazo borrado, otro del
    // vigente) — como ya no tenemos el archivo del que se borró (el backend deja de mandarlo),
    // solo el evento MÁS RECIENTE por raíz puede adjuntar el archivo; el resto queda sin archivo
    // adjunto (solo el texto genérico) en vez de mostrar erróneamente el archivo actual.
    const ultimaSubidaPorRaiz = new Map<number, number>();
    for (const e of entries) {
      if (e.estado !== estadoSubida || e.elementoId == null) continue;
      const raiz = Number(e.elementoId);
      const actualTs = new Date(e.fecha).getTime();
      const previoTs = ultimaSubidaPorRaiz.get(raiz);
      if (previoTs == null || actualTs > previoTs) ultimaSubidaPorRaiz.set(raiz, actualTs);
    }

    const conArchivo = entries.map((e) => {
      const version = e.elementoId != null ? (posicionPorId.get(Number(e.elementoId)) ?? null) : null;
      if (e.estado !== estadoSubida || e.elementoId == null) return { ...e, version };
      const esLaSubidaVigente = ultimaSubidaPorRaiz.get(Number(e.elementoId)) === new Date(e.fecha).getTime();
      if (!esLaSubidaVigente) return { ...e, version };
      const siguiente = cadena.siguientePorId.get(Number(e.elementoId));
      if (!siguiente) return { ...e, version };
      const esVigente =
        (esEvidencia ? siguiente.evidenciaId : siguiente.audioId) === (esEvidencia ? cadena.actual.evidenciaId : cadena.actual.audioId);
      return {
        ...e,
        version,
        archivoAdjunto: { ...archivoDe(siguiente), url: esVigente ? archivoDe(siguiente).url : null, eliminable: esVigente },
      };
    });

    // Si hubo al menos un reemplazo, mostramos que existió un archivo original — sin link,
    // el asesor ya no puede volver a abrirlo (solo el admin conserva ese acceso).
    if (cadena.version.length > 1) {
      const primeraFecha = entries.length > 0 ? new Date(Math.min(...entries.map((e) => new Date(e.fecha).getTime()))) : new Date();
      conArchivo.unshift({
        id: -1 * (esEvidencia ? cadena.inicial.evidenciaId : cadena.inicial.audioId),
        elementoTipo,
        elementoId,
        estado: 'SISTEMA_ARCHIVO_ORIGINAL',
        observacion: null,
        fecha: new Date(primeraFecha.getTime() - 1000).toISOString(),
        ronda: 1,
        version: 1,
        archivoAdjunto: { ...archivoDe(cadena.inicial), url: null },
      });
    }

    return conArchivo;
  };

  // Parse NTCs columns for mini-matrix header rendering
  const columnas = useMemo(() => {
    return (ntcs || [])
      .map((n: any) => ({ ntcId: n.ntcId || n.ntc?.ntcId, nombre: n.nombre || n.ntc?.nombre, orden: n.orden || 1 }))
      .sort((a, b) => a.orden - b.orden);
  }, [ntcs]);

  const reqMap = useMemo(() => {
    const m = new Map<string, any>();
    (requisitos || []).forEach((r) => m.set(r.id, r));
    return m;
  }, [requisitos]);

  const calMap = useMemo(() => {
    const m = new Map<string, string>();
    (requisitos || []).forEach((r) => m.set(r.id, r.calificacion));
    return m;
  }, [requisitos]);

  // Helpers to categorize elements
  const { matrizElements, evidenciaElements, audioElements } = useMemo(() => {
    const matriz: ElementoRechazado[] = [];
    const evs: ElementoRechazado[] = [];
    const aus: ElementoRechazado[] = [];

    elementosRechazados.forEach((el) => {
      if (['REQUISITO', 'OBSERVACIONES', 'PLANMEJORA', 'FECHAIMPLEMENTACION'].includes(el.elementoTipo)) {
        matriz.push(el);
      } else if (el.elementoTipo === 'EVIDENCIA') {
        evs.push(el);
      } else if (el.elementoTipo === 'AUDIO') {
        aus.push(el);
      }
    });

    return { matrizElements: matriz, evidenciaElements: evs, audioElements: aus };
  }, [elementosRechazados]);

  // Helper to determine if an element is resolved — delega en `estaResuelto` (predicado
  // compartido con backend). Un re-upload (reemplazo) también resuelve, ya que genera su
  // propio evento en el historial. Responder al administrador (RESPUESTA_ASESOR) SIEMPRE
  // cuenta como resuelto para el asesor, incluso si el rechazo pedía volver a subir el
  // archivo — el mismo criterio que "enviar a revisión" (ver FinalizarAsesoriaPage).
  const isElementoResuelto = (el: ElementoRechazado) => {
    const key = historialKey(el.elementoTipo, el.elementoId);
    if (clavesResueltas.has(key)) return true;

    const entries = historialPorElemento?.get(key) ?? [];

    if (el.elementoTipo === 'EVIDENCIA') {
      const tieneReemplazo = evidencias.some((ev) => ev.reemplazaEvidenciaId === Number(el.elementoId) && ev.estado !== 'ELIMINADO');
      if (tieneReemplazo) return true;
      const evObj = evidencias.find((ev) => String(ev.evidenciaId) === el.elementoId);
      if (evObj && evObj.estado === 'ELIMINADO') return true;
      return estaResuelto(entries);
    }

    if (el.elementoTipo === 'AUDIO') {
      const tieneReemplazo = audios.some((au) => au.reemplazaAudioId === Number(el.elementoId) && au.estado !== 'ELIMINADO');
      if (tieneReemplazo) return true;
      const auObj = audios.find((au) => String(au.audioId) === el.elementoId);
      if (auObj && (auObj as any).estado === 'ELIMINADO') return true;
      return estaResuelto(entries);
    }

    if (['REQUISITO', 'OBSERVACIONES', 'PLANMEJORA', 'FECHAIMPLEMENTACION'].includes(el.elementoTipo)) {
      return estaResuelto(entries);
    }

    return false;
  };

  // Group matrix elements by terna/grupo for step navigation
  const matrizSteps: MatrizStep[] = useMemo(() => {
    const stepsMap = new Map<string, { ternaDef: any; evalTerna?: any; elements: ElementoRechazado[] }>();

    matrizElements.forEach((el) => {
      let ternaDef: any = null;
      let evalTerna: any = null;

      if (el.elementoTipo === 'REQUISITO') {
        const reqItem = requisitos.find((r) => r.evaluacionRequisito?.id === Number(el.elementoId));
        const reqUuid = reqItem?.id;
        ternaDef = ternas.find((t) => t.requisitoC1Id === reqUuid || t.requisitoC2Id === reqUuid || t.requisitoC3Id === reqUuid);
      } else {
        evalTerna =
          evaluacionesTernas.find((et) => et.id === Number(el.elementoId)) ??
          correccionesCtx?.ternasConCorreccion?.find((et: any) => et.evaluacionTernaId === Number(el.elementoId));
        if (evalTerna) {
          ternaDef = ternas.find((t) => t.id === (evalTerna.ternaId ?? evalTerna.id) || (evalTerna.grupo && t.grupo === evalTerna.grupo));
        }
      }

      if (!ternaDef && el.elementoId) {
        ternaDef = ternas.find((t) => t.id === Number(el.elementoId));
      }

      if (ternaDef) {
        const key = ternaDef.grupo ? `grupo:${ternaDef.grupo}` : `terna:${ternaDef.id}`;
        if (!stepsMap.has(key)) {
          stepsMap.set(key, { ternaDef, evalTerna, elements: [] });
        }
        stepsMap.get(key)!.elements.push(el);
      }
    });

    return [...stepsMap.entries()].map(([key, value]) => {
      const ternaDef = value.ternaDef;
      const rawEval =
        value.evalTerna ??
        evaluacionesTernas.find((et) => et.ternaId === ternaDef?.id || (ternaDef?.grupo && et.grupo === ternaDef.grupo)) ??
        correccionesCtx?.ternasConCorreccion?.find(
          (et: any) => et.ternaId === ternaDef?.id || (ternaDef?.grupo && et.grupo === ternaDef.grupo)
        );
      return {
        key,
        ternaDef,
        evalTerna: rawEval
          ? {
              id: rawEval.id ?? rawEval.evaluacionTernaId,
              ternaId: rawEval.ternaId ?? ternaDef?.id,
              grupo: rawEval.grupo ?? ternaDef?.grupo,
              observaciones: rawEval.observaciones ?? '',
              planMejora: rawEval.planMejora ?? '',
              fechaImplementacion: rawEval.fechaImplementacion ?? null,
            }
          : undefined,
        rejectedElements: value.elements,
      };
    });
  }, [matrizElements, requisitos, ternas, evaluacionesTernas, correccionesCtx]);

  const itemId = searchParams.get('item');
  useEffect(() => {
    if (!itemId) return;
    if (activeSubTab === 'evidencias' && evidenciaElements.length > 0) {
      const idx = evidenciaElements.findIndex((el) => {
        if (String(el.elementoId) === itemId) return true;
        const cadena = cadenasEvidencias.find((c: any) => c.version.some((v: any) => String(v.evidenciaId) === itemId));
        return cadena && String(cadena.actual.evidenciaId) === String(el.elementoId);
      });
      if (idx !== -1) setEvidenciasIndex(idx);
    } else if (activeSubTab === 'audios' && audioElements.length > 0) {
      const idx = audioElements.findIndex((el) => {
        if (String(el.elementoId) === itemId) return true;
        const cadena = cadenasAudios.find((c: any) => c.version.some((v: any) => String(v.audioId) === itemId));
        return cadena && String(cadena.actual.audioId) === String(el.elementoId);
      });
      if (idx !== -1) setAudiosIndex(idx);
    } else if (activeSubTab === 'matriz' && matrizSteps.length > 0) {
      const idx = matrizSteps.findIndex((step) => step.rejectedElements.some((el) => String(el.elementoId) === itemId));
      if (idx !== -1) setMatrizIndex(idx);
    }
  }, [itemId, activeSubTab, evidenciaElements, audioElements, matrizSteps, cadenasEvidencias, cadenasAudios]);

  // Stepper index & active elements resolution
  const { activeElements, currentIndex, setCurrentIndex } = useMemo(() => {
    if (activeSubTab === 'evidencias') {
      return {
        activeElements: evidenciaElements,
        currentIndex: Number.isNaN(evidenciasIndex) || evidenciasIndex >= evidenciaElements.length ? 0 : evidenciasIndex,
        setCurrentIndex: setEvidenciasIndex,
      };
    }
    if (activeSubTab === 'audios') {
      return {
        activeElements: audioElements,
        currentIndex: Number.isNaN(audiosIndex) || audiosIndex >= audioElements.length ? 0 : audiosIndex,
        setCurrentIndex: setAudiosIndex,
      };
    }
    return {
      activeElements: matrizSteps,
      currentIndex: Number.isNaN(matrizIndex) || matrizIndex >= matrizSteps.length ? 0 : matrizIndex,
      setCurrentIndex: setMatrizIndex,
    };
  }, [activeSubTab, matrizSteps, evidenciaElements, audioElements, matrizIndex, evidenciasIndex, audiosIndex]);

  // Strongly-typed step variables for rendering
  const activeMatrizStep = activeSubTab === 'matriz' ? (activeElements[currentIndex] as MatrizStep) : null;

  const handleNext = () => {
    if (currentIndex < activeElements.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const isStepResuelto = (step: any) => {
    if (!step) return false;
    if (activeSubTab === 'matriz') {
      return (step as MatrizStep).rejectedElements.every((el: any) => isElementoResuelto(el));
    }
    return isElementoResuelto(step as ElementoRechazado);
  };

  const handleSaveSuccess = () => {
    if (currentIndex < activeElements.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* ── Sub-Tab Bar ── */}
      <div className="flex gap-1 border-b border-[#ede9e3] pb-2">
        <button
          type="button"
          onClick={() => {
            setActiveSubTab('matriz');
          }}
          className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
            activeSubTab === 'matriz' ? 'bg-[#1a1a1a] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Matriz de la asesoría ({matrizSteps.length})
        </button>
        <button
          type="button"
          onClick={() => {
            setActiveSubTab('evidencias');
          }}
          className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
            activeSubTab === 'evidencias' ? 'bg-[#1a1a1a] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Evidencias ({evidenciaElements.length})
        </button>
        <button
          type="button"
          onClick={() => {
            setActiveSubTab('audios');
          }}
          className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
            activeSubTab === 'audios' ? 'bg-[#1a1a1a] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Audios ({audioElements.length})
        </button>
      </div>

      {activeElements.length === 0 ? (
        <div className="admin-card p-12 text-center text-gray-500">
          <CheckCircle className="w-8 h-8 text-[#2d7a4f] mx-auto mb-2 opacity-55" />
          <p className="text-[13px] font-medium">No hay correcciones pendientes en esta sección.</p>
        </div>
      ) : activeSubTab === 'matriz' ? (
        <div className="admin-card border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm flex flex-col justify-between min-h-112.5">
          {/* Progress Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50/50">
            <button
              type="button"
              onClick={handlePrev}
              disabled={currentIndex === 0}
              className="flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              Anterior
            </button>
            <div className="flex flex-col items-center gap-0.5">
              <span className="text-xs font-bold text-gray-700">
                Paso {currentIndex + 1} de {activeElements.length}
              </span>
              <div className="flex gap-1 mt-0.5">
                {activeElements.map((el, i) => {
                  const resuelto = isStepResuelto(el);
                  return (
                    <span
                      key={(el as MatrizStep).key}
                      className={`block h-1 rounded-full transition-all ${
                        i === currentIndex ? 'w-4 bg-gray-700' : resuelto ? 'w-1.5 bg-[#2d7a4f]' : 'w-1.5 bg-red-400'
                      }`}
                    />
                  );
                })}
              </div>
            </div>
            <button
              type="button"
              onClick={handleNext}
              disabled={currentIndex === activeElements.length - 1}
              className="flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              Siguiente
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Stepper Content Area */}
          <div className="p-6 flex-1 flex flex-col justify-between">
            <div>
              {activeMatrizStep && (
                /* ── MATRIZ TAB: GROUPED STEP LAYOUT (COMPLETELY TernaRevisionStepper-BASED) ── */
                <div className="space-y-4">
                  {/* Step Header */}
                  <div className="flex justify-between items-start">
                    {isStepResuelto(activeMatrizStep) && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-[#f0fdf4] text-[#166534] border border-[#bbf7d0]">
                        <CheckCircle className="w-3.5 h-3.5" /> Resuelto
                      </span>
                    )}
                  </div>
                  {showSecretPanel && activeMatrizStep && (
                    <div className="text-[11px] font-mono text-amber-800 bg-amber-50 px-3 py-1.5 rounded-lg border border-amber-200 flex flex-wrap items-center gap-3">
                      <span className="font-bold">DEBUG IDs:</span>
                      {activeMatrizStep.ternaDef?.id && (
                        <span>
                          TernaID: <strong>#{activeMatrizStep.ternaDef.id}</strong>
                          {activeMatrizStep.ternaDef.grupo ? ` (Grupo ${activeMatrizStep.ternaDef.grupo})` : ''}
                        </span>
                      )}
                      {activeMatrizStep.evalTerna?.id && (
                        <span>
                          EvalTernaID: <strong>#{activeMatrizStep.evalTerna.id}</strong>
                        </span>
                      )}
                      <span>
                        Elementos:{' '}
                        <strong>{activeMatrizStep.rejectedElements.map((e) => `${e.elementoTipo}:${e.elementoId}`).join(', ')}</strong>
                      </span>
                    </div>
                  )}

                  <TernaStepCorrectionForm
                    key={activeMatrizStep.key}
                    step={activeMatrizStep}
                    nit={nit}
                    asesoriaId={asesoriaId}
                    ternas={ternas}
                    refetch={refetch}
                    onResponded={onResponded}
                    setCampoReq={setCampoReq}
                    columnas={columnas}
                    reqMap={reqMap}
                    calMap={calMap}
                    onSaveSuccess={handleSaveSuccess}
                    historialPorElemento={historialPorElemento}
                    cambioFieldLogs={cambioFieldLogs}
                    permiteSubirEvidenciasNuevas={permiteSubirEvidenciasNuevas}
                    showSecretPanel={showSecretPanel}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* ── EVIDENCIAS / AUDIOS TAB: layout split compartido con el admin (lista + conversación) ── */
        <CorreccionesSplitPanel
          items={activeElements as ElementoRechazado[]}
          selectedIndex={currentIndex}
          onSelect={setCurrentIndex}
          getKey={(item) => item.id}
          resetKey={activeSubTab}
          renderItem={(item) => {
            const entries = historialPorElemento?.get(historialKey(item.elementoTipo, item.elementoId)) ?? [];
            return (
              <>
                <EstadoDot estado={estadoConversacion(entries)} />
                <span className="flex-1 min-w-0 truncate text-[12px] font-medium text-gray-800">
                  {item.elementoNombre || `${LABEL_TIPO[item.elementoTipo]} sin nombre`}
                </span>
                {showSecretPanel && (
                  <span className="font-mono text-[10px] text-amber-700 bg-amber-50 px-1 py-0.5 rounded border border-amber-200 select-all shrink-0 font-semibold">
                    #{item.elementoId}
                  </span>
                )}
                {isElementoResuelto(item) && <CheckCircle data-testid="cc-item-resuelto" className="w-3.5 h-3.5 text-[#2d7a4f] shrink-0" />}
                <PendingBadge count={entries.length} />
              </>
            );
          }}
          renderPanel={(activeEl) => (
            <div key={activeEl.id} className="flex flex-col gap-4">
              <div className="flex justify-between items-start">
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-red-600 bg-red-50 px-2 py-0.5 rounded border border-red-100">
                    {LABEL_TIPO[activeEl.elementoTipo]}
                  </span>
                  <h3 className="font-bold text-[16px] text-gray-900 mt-1">{activeEl.elementoNombre || 'Elemento a corregir'}</h3>
                  {showSecretPanel && (
                    <div className="text-[11px] font-mono text-amber-800 bg-amber-50 px-2.5 py-1 rounded-md border border-amber-200 inline-flex flex-wrap items-center gap-2 mt-1">
                      <span className="font-bold">DEBUG ID:</span>
                      <span>
                        ElementoID: <strong>#{activeEl.elementoId}</strong>
                      </span>
                      <span>
                        Tipo: <strong>{activeEl.elementoTipo}</strong>
                      </span>
                      {activeEl.requisitoId && (
                        <span>
                          RequisitoID: <strong>#{activeEl.requisitoId}</strong>
                        </span>
                      )}
                    </div>
                  )}
                </div>
                {isStepResuelto(activeEl) && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-[#f0fdf4] text-[#166534] border border-[#bbf7d0]">
                    <CheckCircle className="w-3.5 h-3.5" /> Resuelto
                  </span>
                )}
              </div>

              {/* ── Zona de acción de la corrección (formulario existente, no es turno de chat) ── */}
              <div data-testid="cc-action-area">
                {activeEl.elementoTipo === 'EVIDENCIA' ? (
                  <EvidenciaCorrectionForm
                    el={activeEl}
                    nit={nit}
                    evidencias={evidencias}
                    cadenasEvidencias={cadenasEvidencias}
                    setIsModalOpen={setIsModalOpen}
                    setPrefillNombre={setPrefillNombre}
                    setReemplazaId={setReemplazaId}
                    onEliminarEvidencia={onEliminarEvidencia}
                    historialPorElemento={historialPorElemento}
                  />
                ) : (
                  <AudioCorrectionForm
                    el={activeEl}
                    nit={nit}
                    asesoriaId={asesoriaId}
                    audios={audios}
                    refetch={refetch}
                    onResponded={onResponded}
                    historialPorElemento={historialPorElemento}
                    onEliminarAudio={onEliminarAudio}
                  />
                )}
              </div>

              {/* ── Conversación (reemplaza el cuadro único "Aclaración o respuesta") ── */}
              <div className="border-t border-gray-100 pt-4 mt-2">
                <CorreccionesChat
                  entries={entriesConArchivo(activeEl.elementoTipo, activeEl.elementoId)}
                  actorActual="asesor"
                  canReply={puedeComentarAsesor(entriesConArchivo(activeEl.elementoTipo, activeEl.elementoId))}
                  comentariosRestantes={comentariosRestantesRonda(entriesConArchivo(activeEl.elementoTipo, activeEl.elementoId))}
                  permiteSubirEvidenciasNuevas={permiteSubirEvidenciasNuevas}
                  onReply={async (texto) => {
                    await asesoriasService.responderRevision(nit, asesoriaId, {
                      elementoTipo: activeEl.elementoTipo,
                      elementoId: activeEl.elementoId,
                      respuesta: texto,
                    });
                    onResponded?.();
                    await refetch();
                  }}
                  onEditReply={async (texto) => {
                    await asesoriasService.editarRespuesta(nit, asesoriaId, {
                      elementoTipo: activeEl.elementoTipo,
                      elementoId: activeEl.elementoId,
                      respuesta: texto,
                    });
                    onResponded?.();
                    await refetch();
                  }}
                  onEliminarReemplazo={
                    // Las evidencias ya no admiten "eliminar": el borrado lógico solo ocurre al
                    // reemplazar (ver EvidenciaCorrectionForm). El audio sí conserva eliminar +
                    // volver a subir, porque no arrastra asociaciones con la matriz/requisitos.
                    activeEl.elementoTipo === 'AUDIO'
                      ? async () => {
                          const audId = Number(activeEl.elementoId);
                          const reemplazo = audios.find((a: any) => a.reemplazaAudioId === audId);
                          if (!reemplazo) return;
                          await asesoriasService.vincularAudioReemplazo(nit, asesoriaId, reemplazo.audioId, null);
                          onResponded?.();
                          await refetch();
                        }
                      : undefined
                  }
                />
              </div>
            </div>
          )}
        />
      )}

      {/* Modal for NuevaEvidencia */}
      <NuevaEvidenciaModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setPrefillNombre(undefined);
          setReemplazaId(undefined);
        }}
        nit={nit}
        asesoriaId={asesoriaId}
        onSuccess={async () => {
          await refetch();
          onResponded?.();
        }}
        prefillNombre={prefillNombre}
        reemplazaEvidenciaId={reemplazaId}
      />
    </div>
  );
}
